import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Shuffle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose,
} from "@/components/ui/drawer";

const CATS = ["All", "Romantic", "Cozy", "Movie Night", "Game Night", "Video Call"];

export default function Dates() {
  const [ideas, setIdeas] = useState([]);
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/date-ideas").then((r) => setIdeas(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (cat === "All" ? ideas : ideas.filter((i) => i.category === cat)),
    [ideas, cat]
  );

  const surprise = () => {
    if (!ideas.length) return;
    const pick = ideas[Math.floor(Math.random() * ideas.length)];
    setSelected(pick);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-primary">Together, apart</div>
          <h1 className="font-serif text-3xl font-semibold">Virtual date ideas</h1>
        </div>
        <Button data-testid="surprise-btn" onClick={surprise} size="sm" className="rounded-full gap-1">
          <Shuffle className="h-4 w-4" /> Surprise
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
        {CATS.map((c) => (
          <button
            key={c} data-testid={`cat-${c.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setCat(c)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
              cat === c ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border/70"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3">
        {filtered.map((idea, i) => (
          <motion.button
            key={idea.id} data-testid={`date-idea-${idea.id}`}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            onClick={() => setSelected(idea)}
            className="text-left rounded-3xl bg-card border border-border/70 p-4 flex items-center gap-4 active:scale-[0.99] transition-transform hover:shadow-md"
          >
            <div className="h-14 w-14 shrink-0 rounded-2xl bg-accent flex items-center justify-center text-2xl">{idea.emoji}</div>
            <div className="min-w-0">
              <div className="font-serif text-lg font-semibold leading-tight">{idea.title}</div>
              <div className="flex gap-1.5 mt-1">
                <span className="rounded-full bg-secondary text-secondary-foreground px-2 py-0.5 text-[10px] font-semibold">{idea.category}</span>
                <span className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] font-semibold">{idea.effort}</span>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DrawerContent className="max-w-md mx-auto">
          {selected && (
            <>
              <DrawerHeader>
                <div className="h-16 w-16 rounded-2xl bg-accent flex items-center justify-center text-3xl mb-2">{selected.emoji}</div>
                <DrawerTitle className="font-serif text-2xl">{selected.title}</DrawerTitle>
                <DrawerDescription className="sr-only">Date idea details</DrawerDescription>
              </DrawerHeader>
              <div className="px-4 pb-2">
                <div className="flex gap-2 mb-3">
                  <span className="rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-xs font-semibold">{selected.category}</span>
                  <span className="rounded-full bg-muted text-muted-foreground px-3 py-1 text-xs font-semibold">{selected.effort}</span>
                </div>
                <p className="text-base leading-relaxed text-foreground/90">{selected.desc}</p>
              </div>
              <DrawerFooter>
                <Button
                  data-testid="plan-date-btn"
                  onClick={() => { toast.success(`Great pick — plan "${selected.title}" together! 💌`); setSelected(null); }}
                  className="rounded-full h-12 font-semibold"
                >
                  <Sparkles className="h-4 w-4 mr-1" /> Let's plan this
                </Button>
                <DrawerClose asChild>
                  <Button variant="ghost" className="rounded-full">Maybe later</Button>
                </DrawerClose>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
